import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { fileUrl } from "@/lib/api";
import { Heart, ExternalLink, QrCode, Sparkles } from "lucide-react";

export default function DonatePage() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/public/donations").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  if (!data) return <div className="max-w-3xl mx-auto px-6 py-32 text-slate-500">{t("common.loading")}</div>;

  const hasContent = data.intro || data.qr_path || data.paypal_url || (data.project_highlights || []).length > 0 || data.bank_info;

  return (
    <div data-testid="donate-page" className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        {/* Left side: intro + projects */}
        <aside className="lg:col-span-7">
          <div className="text-xs uppercase tracking-[0.3em] text-bordeaux-accent mb-4">
            {t("donate.eyebrow")}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
            {t("donate.title1")}<br /> {t("donate.title2")}
          </h1>

          <p className="mt-8 text-lg text-slate-600 leading-relaxed max-w-xl whitespace-pre-line">
            {data.intro || t("donate.defaultIntro")}
          </p>

          {(data.project_highlights || []).length > 0 && (
            <div className="mt-12" data-testid="project-highlights">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2">
                <Sparkles size={13} className="text-bordeaux" />
                {t("donate.projectsTitle")}
              </div>
              <ul className="space-y-3">
                {data.project_highlights.map((p, i) => (
                  <li key={i} data-testid={`project-${i}`} className="flex items-start gap-3 text-slate-700">
                    <span className="mt-2 w-1.5 h-1.5 bg-bordeaux rounded-full flex-shrink-0" />
                    <span className="leading-relaxed">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.bank_info && (
            <div className="mt-12 p-6 border border-slate-200 rounded-sm bg-beige-light" data-testid="bank-info">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">{t("donate.bankTitle")}</div>
              <pre className="text-sm text-slate-700 font-sans whitespace-pre-line leading-relaxed">{data.bank_info}</pre>
            </div>
          )}

          <div className="mt-12 p-6 border border-bordeaux/20 rounded-sm bg-beige-light">
            <div className="text-xs uppercase tracking-widest text-bordeaux-accent mb-2">{t("donate.trustTitle")}</div>
            <p className="text-xs text-slate-600 leading-relaxed">{t("donate.trustText")}</p>
          </div>
        </aside>

        {/* Right side: QR + PayPal */}
        <section className="lg:col-span-5 lg:sticky lg:top-28">
          <div className="bg-white border border-slate-200 rounded-sm p-8 md:p-10 text-center" data-testid="donate-card">
            <Heart className="mx-auto text-bordeaux mb-5" size={32} fill="currentColor" />
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
              {t("donate.qrTitle")}
            </h2>
            <p className="mt-3 text-sm text-slate-600 max-w-xs mx-auto">
              {t("donate.qrSubtitle")}
            </p>

            <div className="mt-8 mx-auto w-64 h-64 max-w-full grid place-items-center bg-beige-light border border-slate-200 rounded-sm overflow-hidden">
              {data.qr_path ? (
                <img
                  src={fileUrl(data.qr_path)}
                  alt="PayPal QR Code"
                  data-testid="qr-image"
                  className="w-full h-full object-contain p-3"
                />
              ) : (
                <div className="flex flex-col items-center text-slate-400 text-xs px-6" data-testid="qr-placeholder">
                  <QrCode size={56} className="mb-3" />
                  <span className="text-center leading-relaxed">{t("donate.qrComingSoon")}</span>
                </div>
              )}
            </div>

            {data.paypal_url && (
              <a
                href={data.paypal_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="paypal-link"
                className="mt-8 inline-flex items-center justify-center gap-2 bg-bordeaux hover:bg-bordeaux-dark text-beige-light rounded-sm px-6 py-3 font-medium text-sm transition-colors w-full"
              >
                {t("donate.paypalBtn")} <ExternalLink size={14} />
              </a>
            )}

            <p className="mt-6 text-[11px] uppercase tracking-widest text-slate-400">
              {t("donate.footnote")}
            </p>
          </div>
        </section>
      </div>

      {!hasContent && (
        <div className="mt-12 text-center text-slate-500 text-sm" data-testid="donate-empty">
          {t("donate.notConfigured")}
        </div>
      )}
    </div>
  );
}
